import { Vector3 } from 'three'
import { EntityType } from '@/plugins/cad/module/CADModule/model/common/constant'
import type { IPoint } from '@/plugins/cad/module/CADModule/model/common/interface'
import { BaseEntity } from './BaseEntity'

/**
 * 对齐长度标注实体，沿 p1-p2 方向测量两点间距离。
 */
export class AlignDimensionEntity extends BaseEntity {
  /** 标注起点 */
  p1!: IPoint
  /** 标注终点 */
  p2!: IPoint
  /** 标注线经过的偏移点，用于确定标注偏移方向与距离 */
  offsetPoint!: IPoint
  /** 标注文字内容，为空时使用自动计算的距离 */
  dimensionText: string = ''
  /** 标注文字位置 */
  textPosition!: IPoint
  /** 标注文字位置是否被手动编辑过 */
  isTextPositionEdited: boolean = false
  /** 标注文字高度，单位毫米 */
  textHeight: number = 2.5

  protected readonly serializeKeys: string[] = [
    'p1',
    'p2',
    'offsetPoint',
    'dimensionText',
    'textPosition',
    'isTextPositionEdited',
    'textHeight',
  ]

  constructor(data: Partial<AlignDimensionEntity>) {
    super(EntityType.AlignDimension)
    Object.assign(this, data)
  }

  /**
   * 获取捕捉数据，返回标注的两个端点作为捕捉点。
   *
   * @returns 包含端点数组的捕捉数据
   */
  getCaptureData() {
    const endPoints: IPoint[] = [
      { x: this.p1.x, y: this.p1.y },
      { x: this.p2.x, y: this.p2.y },
    ]

    return { endPoints }
  }
}

/** 对齐标注延伸线起点与端点之间的预留间隙，单位米 */
export const ALIGN_DIMENSION_EXTENSION_LINE_GAP = 0
/** 对齐标注延伸线越过标注线的延伸量，单位米 */
export const ALIGN_DIMENSION_EXTENSION_LINE_EXTEND = 1

/**
 * 根据测量两端点与偏移点，计算对齐长度标注的线段端点与几何参数。
 *
 * @param p1 - 标注起点
 * @param p2 - 标注终点
 * @param p3 - 偏移参考点，用于确定标注线偏移方向
 * @param gap - 测量端点到延伸线起点的预留间隙，单位米
 * @param ext - 标注线端点沿法向继续延伸的长度，单位米
 * @returns 标注线端点、延伸线端点、偏移距离、标注长度与旋转角度
 */
export function getAlignDimensionPoints(
  p1: Vector3,
  p2: Vector3,
  p3: Vector3,
  gap = ALIGN_DIMENSION_EXTENSION_LINE_GAP,
  ext = ALIGN_DIMENSION_EXTENSION_LINE_EXTEND
) {
  // 1. 方向与单位法向量
  const v = new Vector3().subVectors(p2, p1)
  const n = new Vector3(-v.y, v.x, 0).normalize()

  // 2. 计算偏移距离 d
  const w = new Vector3().subVectors(p3, p1)
  const d = w.dot(n)
  const sign = d >= 0 ? 1 : -1

  // 3. 标注线端点
  const d1 = p1.clone().add(n.clone().multiplyScalar(d))
  const d2 = p2.clone().add(n.clone().multiplyScalar(d))

  // 4. 延伸线端点
  const e1_start = p1.clone().add(n.clone().multiplyScalar(gap * sign))
  const e1_end = d1.clone().add(n.clone().multiplyScalar(ext * sign))

  const e2_start = p2.clone().add(n.clone().multiplyScalar(gap * sign))
  const e2_end = d2.clone().add(n.clone().multiplyScalar(ext * sign))

  // 5. 标注的长度、旋转角度
  const length = p1.distanceTo(p2)
  const angle = Math.atan2(v.y, v.x)

  return { d1, d2, e1_start, e1_end, e2_start, e2_end, d, length, angle }
}
